import IonIcon from '@reacticons/ionicons'
import { GetStaticProps, NextPage } from 'next'
import Image from 'next/image'
import Link from 'next/link'
import React from 'react'
import XTwitterLogo from 'src/assets/X-Twitter.svg'
import { Footer } from 'src/components/Footer/Footer'
import { Breadcrumb } from 'src/components/global/Breadcrumb'
import { ExpertPanel } from 'src/components/global/ExpertPanel'
import MainContainer from 'src/components/global/MainContainer/MainContainer'
import { ThemeOnly } from 'src/components/global/ThemeOnly'
import { THEME } from 'src/config/config'
import { apolloSdk } from 'src/graphql/apolloSdk'
import { IAuthorFullItemFragment } from 'src/graphql/generated/hooks'
import { useDefaultLogo } from 'src/hooks/useDefaultLogo'
import { Meta } from 'src/layout/Meta'
import { Main } from 'src/templates/Main'
import { getFullName } from 'src/utils/getFullName'

type Props = {
  author: IAuthorFullItemFragment
}

const AuthorPage: NextPage<Props> = ({ author }: Props) => {
  const firstName = author.attributes?.firstName || ''
  const lastName = author.attributes?.lastName || ''
  const jobTitle = author.attributes?.jobTitle || ''
  const company = author.attributes?.company || ''
  const title = getFullName([jobTitle, company])
  const slug = author.attributes?.slug || ''
  const bio = author.attributes?.bio || ''

  const fullName = `${firstName} ${lastName}`
  const description = `${fullName} - ${title} ${bio}`
  const meta = <Meta title={`${fullName} - ${title}`} url={`/lth-authors/${slug}/`} description={description} />
  const socialLink = (icon: React.ReactNode, link?: string | null) => {
    return link ? (
      <div className="cursor-pointer ml-3 w-[40px] h-[40px] rounded-[20px] bg-navy flex justify-center items-center">
        <Link href={link} passHref className="flex" target="_blank">
          {icon}
        </Link>
      </div>
    ) : null
  }
  const avatar = useDefaultLogo(author.attributes?.avatar?.data?.attributes?.url)
  return (
    <Main meta={meta}>
      <MainContainer id="mainContainer">
        <div className="flex flex-col bg-clouds">
          <Breadcrumb className="justify-start" subject="MEET THE EXPERT" subtitle={fullName} />
          <div className="px-5 md:pl-0 !max-w-[1100px] w-full mx-auto flex flex-col md:flex-row items-center py-2 md:pb-0">
            <div className="flex-1 flex-col md:flex-row flex my-[1rem] md:my-[4rem]">
              <div className="flex flex-col items-center justify-center mb-5 md:mb-0">
                <div className="flex justify-center items-center w-full md:h-[100px]">
                  <Image
                    src={avatar}
                    alt="avatar"
                    className="object-fit-cover rounded-[70px]"
                    priority
                    width={140}
                    height={140}
                  />
                </div>
              </div>
              <div className="flex flex-col md:flex-1 md:ml-[37px] items-center md:items-start justify-center">
                <h1 className="font-bold text-[2rem]">{fullName}</h1>
                <h2 className="text-[1rem]">{title}</h2>
              </div>
            </div>
            <div className="flex-1 flex flex-row justify-end ml-[-10px] md:ml-0">
              {socialLink(
                <Image src={XTwitterLogo} alt="X-Twitter" className="cursor-pointer" width={14} height={14} />,
                author.attributes?.twitter,
              )}
              {socialLink(
                <IonIcon className="text-white cursor-pointer" name="logo-linkedin" />,
                author.attributes?.linkedin,
              )}
            </div>
          </div>
        </div>
        <div
          className="px-5 md:pl-0 md:pr-[1.25rem] !max-w-[1100px] w-full mx-auto flex flex-row items-center py-10"
          style={{ alignItems: 'flex-start', justifyContent: 'space-between' }}
        >
          <ExpertPanel className="w-full mb-8" title="About" content={bio} isHtml={true} />
        </div>
        <ThemeOnly theme="legaltech">
          <Footer />
        </ThemeOnly>
      </MainContainer>
    </Main>
  )
}
export const getStaticProps: GetStaticProps<Props> = async ({ params }) => {
  if (!params?.slug || THEME === 'ilta') {
    return {
      revalidate: 60,
      notFound: true,
    }
  }
  const slug = params.slug as string
  try {
    // const { slugUpdates } = await apolloSdk.getSlugUpdates({ type: 'authors', oldSlug: slug })
    // if (slugUpdates && slugUpdates?.data.length > 0) {
    //   slug = slugUpdates.data[0].attributes?.newSlug || slug
    // }
    const { authors } = await apolloSdk.authorBySlug({ slug })
    if (!authors || !authors.data || authors.data.length === 0) {
      return {
        revalidate: 60,
        notFound: true,
      }
    }
    return {
      props: {
        author: authors.data[0],
      },
      revalidate: 60,
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error occurred in getStaticProps of Author:', error)
    // Graceful fallback in case of an upstream error
    return {
      notFound: true,
      revalidate: 60, // Reattempt fetching during the next static regeneration
    }
  }
}

export async function getStaticPaths() {
  const { authors } = await apolloSdk.allAuthors()
  const paths: Array<{ params: { slug: string } }> = []
  if (authors) {
    authors.data.forEach((author) => {
      if (!author) return
      paths.push({ params: { slug: author.attributes?.slug || '' } })
    })
  }

  return {
    paths,
    fallback: 'blocking',
  }
}

export default AuthorPage
